/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

/**
 * Outlet Demand Calendar — Vorion Revenue Command Center
 *
 * Two automated tabs:
 *   1) Demand Calendar  → 12 months × up to 31 days; user enters Event,
 *                         AI Score (0-100), AI Action per day. Score colour-
 *                         codes the cell using the AI Demand Legend.
 *   2) Occupancy & RevPASH → Per (Outlet × Meal Period) row. User enters
 *                         Seats, Operating Hours, Avg Dining Duration,
 *                         Covers, Net Revenue. Component auto-computes APC,
 *                         Max Turn Capacity, Seat Occupancy %, Table Turns,
 *                         RevPASH, Status badge and AI Recommendation.
 *
 * Persistence: all values flow through `data` / `onChange(key, value)` props
 * so they live alongside the rest of the F&B profile (`biz_odc_*` keys).
 */

const MONTHS = [
  { id: "jan", label: "Jan", days: 31, monthIdx: 0 },
  { id: "feb", label: "Feb", days: 28, monthIdx: 1 },
  { id: "mar", label: "Mar", days: 31, monthIdx: 2 },
  { id: "apr", label: "Apr", days: 30, monthIdx: 3 },
  { id: "may", label: "May", days: 31, monthIdx: 4 },
  { id: "jun", label: "Jun", days: 30, monthIdx: 5 },
  { id: "jul", label: "Jul", days: 31, monthIdx: 6 },
  { id: "aug", label: "Aug", days: 31, monthIdx: 7 },
  { id: "sep", label: "Sep", days: 30, monthIdx: 8 },
  { id: "oct", label: "Oct", days: 31, monthIdx: 9 },
  { id: "nov", label: "Nov", days: 30, monthIdx: 10 },
  { id: "dec", label: "Dec", days: 31, monthIdx: 11 },
];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

// Leap-year aware Feb day count
function daysInMonth(year, monthIdx0) {
  return new Date(year, monthIdx0 + 1, 0).getDate();
}

const MEAL_PERIODS = ["Breakfast", "Lunch", "High Tea", "Dinner", "Midnight", "Custom1", "Custom2"];

// User-friendly default display labels (slot IDs above are stable storage keys).
// Custom1/Custom2 labels are user-renameable — see `getMealLabel` below.
const MEAL_DEFAULT_LABELS = {
  Breakfast: "Breakfast",
  Lunch:     "Lunch",
  "High Tea":"High Tea",
  Dinner:    "Dinner",
  Midnight:  "Midnight",
  Custom1:   "Custom 1",
  Custom2:   "Custom 2",
};

// Only these meal slots expose an inline "rename" input.
const RENAMEABLE_MEALS = new Set(["Custom1", "Custom2"]);

function getMealLabel(data, mealId) {
  const override = data?.[`biz_odc_meal_label_${mealId}`];
  if (typeof override === "string" && override.trim()) return override.trim();
  return MEAL_DEFAULT_LABELS[mealId] || mealId;
}

const DEFAULT_OUTLETS = [
  { id: "outlet_kmcy",         name: "KMCY" },
  { id: "outlet_feast",        name: "Feast" },
  { id: "outlet_chingari",     name: "Chingari" },
  { id: "outlet_lobby_lounge", name: "Lobby Lounge" },
];

const DEFAULT_SEATING = {
  // Seats / Operating Hours / Avg Dining Duration (hrs) by meal — used as
  // initial values, fully editable.
  Breakfast: { seats: 160, hours: 3.5, duration: 0.75 },
  Lunch:     { seats: 90,  hours: 3,   duration: 1.25 },
  "High Tea":{ seats: 90,  hours: 2,   duration: 1 },
  Dinner:    { seats: 90,  hours: 4,   duration: 1.5 },
  Midnight:  { seats: 60,  hours: 3,   duration: 1.25 },
  Custom1:   { seats: 60,  hours: 2,   duration: 1 },
  Custom2:   { seats: 60,  hours: 2,   duration: 1 },
};

// Sample 2026 demand-calendar events lifted from the user's Excel template.
// Loaded only when the user clicks "Load sample events"; never overwrites
// data already entered.
const SAMPLE_EVENTS = {
  jan: {
    1: { event: "New Year demand | premium dining, cocktails, brunch", score: 95, action: "Premium set menus, late checkout brunch, upsell cocktails, staffing +20%" },
    2: { event: "Corporate return + expatriate movement",              score: 70, action: "Push business lunches, chef specials, weekday beverage offers" },
    26: { event: "Republic Day | family brunch + local demand",        score: 82, action: "Themed brunch, family packages, Indian specials" },
  },
  feb: {
    14: { event: "Valentine's | couples + premium dining", score: 96, action: "Couple packages, tasting menus, romantic table inventory, desserts" },
  },
  mar: {
    4: { event: "Holi | brunch, chaat, kebabs, cocktails", score: 88, action: "Holi brunch, colour-safe outdoor activation, pre-booking push" },
    21: { event: "Eid al-Fitr | family dining",            score: 84, action: "Family platters, biryani/kebab specials, takeaway packs" },
    27: { event: "Ram Navami | vegetarian/family demand",  score: 75, action: "Satvik/veg specials, family lunches" },
  },
  apr: {
    3:  { event: "Good Friday | long weekend dining",     score: 78, action: "Seafood/global specials, early dinner promotions" },
    20: { event: "Summer heat | lower lunch walk-ins",    score: 55, action: "Coolers, lighter menus, indoor comfort offers" },
  },
  may: {
    1:  { event: "Labour Day + Buddha Purnima | long weekend", score: 82, action: "Family brunch, high tea, local heritage menus" },
    10: { event: "Mother's Day | family dining",                score: 90, action: "Mother's Day brunch, dessert platters, gifts" },
  },
  jun: {
    1:  { event: "Pre-monsoon + school reopening | soft demand", score: 52, action: "Value lunches, delivery/takeaway combos" },
    21: { event: "Father's Day | family + premium grill",        score: 86, action: "Steak/grill/thali premium specials" },
    25: { event: "Muharram | sensitive demand",                  score: 55, action: "Avoid loud promotions, focus on quiet family service" },
  },
  jul: {
    1: { event: "Monsoon comfort demand | chai, snacks, AYCE", score: 70, action: "High tea combo, momos, soups, comfort menu" },
  },
  aug: {
    15: { event: "Independence Day | family brunch + staycation", score: 86, action: "Tri-colour brunch, family meal packs, staycation tie-ins" },
    27: { event: "Eid-e-Milad | family dining",                    score: 72, action: "Family platters, kebabs/biryani specials" },
  },
  sep: {
    7:  { event: "Ganesh Chaturthi | Pune peak local demand", score: 92, action: "Modak / regional sweets, family thalis, delivery packs" },
    17: { event: "Onam (south) | family festive demand",       score: 78, action: "Sadhya / banana-leaf specials, family bookings" },
  },
  oct: {
    2:  { event: "Gandhi Jayanti | dry day risk",          score: 60, action: "Veg menu push, non-alcohol pairings, family combos" },
    11: { event: "Dussehra | festive family demand",       score: 84, action: "Festive thalis, sweets, pre-bookings" },
    23: { event: "Diwali week | gifting + family brunches", score: 95, action: "Premium menus, gifting hampers, advance booking push" },
  },
  nov: {
    14: { event: "Children's Day | family + kids menu",  score: 70, action: "Kids menu, dessert specials, family bookings" },
    24: { event: "Guru Nanak Jayanti | family dining",   score: 72, action: "Punjabi specials, family thalis" },
  },
  dec: {
    24: { event: "Christmas Eve | premium dining",      score: 96, action: "Gala dinner, brunch, bakery, festive beverages" },
    25: { event: "Christmas Day | premium dining",      score: 96, action: "Gala brunch, set menus, premium bar" },
    31: { event: "New Year's Eve | maximum demand",     score: 100, action: "Gala pricing, limited inventory, premium packages, prepayment policy" },
  },
};

// ---------- helpers ----------
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v, digits = 0) =>
  Number.isFinite(v) && v !== 0 ? v.toLocaleString(undefined, { maximumFractionDigits: digits }) : "—";

const fmtPct = (v) =>
  Number.isFinite(v) && v !== 0 ? `${(v * 100).toFixed(1)}%` : "—";

// Score → tailwind classes (dark-theme friendly)
function scoreClass(score) {
  if (!Number.isFinite(score) || score <= 0) return "bg-transparent border-zinc-800 text-zinc-400";
  if (score >= 90) return "bg-[#10B981]/20 border-[#10B981]/40 text-[#86EFAC]";
  if (score >= 80) return "bg-[#38BDF8]/20 border-[#38BDF8]/40 text-[#7DD3FC]";
  if (score >= 60) return "bg-[#F59E0B]/15 border-[#F59E0B]/40 text-[#FCD34D]";
  return "bg-[#EF4444]/15 border-[#EF4444]/40 text-[#FCA5A5]";
}
function scoreLevel(score) {
  if (!Number.isFinite(score) || score <= 0) return "—";
  if (score >= 90) return "Very High";
  if (score >= 80) return "High";
  if (score >= 60) return "Medium";
  return "Low";
}

// Occupancy status badge
function occStatus(pct) {
  if (!Number.isFinite(pct) || pct <= 0) return { label: "—", class: "bg-zinc-800 text-zinc-400" };
  if (pct >= 0.85) return { label: "Peak / Protect",  class: "bg-[#EF4444]/20 text-[#FCA5A5] border border-[#EF4444]/40" };
  if (pct >= 0.6)  return { label: "Healthy",         class: "bg-[#10B981]/20 text-[#86EFAC] border border-[#10B981]/40" };
  return                  { label: "Underutilized",   class: "bg-[#F59E0B]/15 text-[#FCD34D] border border-[#F59E0B]/40" };
}
function occRecommendation(pct) {
  if (!Number.isFinite(pct) || pct <= 0) return "Enter covers & revenue to see AI recommendation";
  if (pct >= 0.85) return "Protect service quality; manage waitlist & raise minimum spends";
  if (pct >= 0.6)  return "Healthy demand — push upsells (desserts, cocktails), reservations";
  return "Underutilized — promote slot, run combos, target outreach, optimise staff";
}

// ---------- key helpers ----------
const LEGACY_YEAR_PLACEHOLDER = "__legacy__";
const k = {
  loc:   "biz_odc_location",
  outletName: "biz_odc_outlet_name",
  cuisine: "biz_odc_cuisine",
  year:  "biz_odc_year",
  yearsList: "biz_odc_years",
  outletsList: "biz_odc_outlets",
  // Year-scoped event/score/action keys
  event:  (yr, m, d) => `biz_odc_event_${yr}_${m}_${d}`,
  score:  (yr, m, d) => `biz_odc_score_${yr}_${m}_${d}`,
  action: (yr, m, d) => `biz_odc_action_${yr}_${m}_${d}`,
  // Legacy (pre-multi-year) keys — read-only fallback for the user's
  // historical 2026 entries that pre-date the year scoping.
  legacyEvent:  (m, d) => `biz_odc_event_${m}_${d}`,
  legacyScore:  (m, d) => `biz_odc_score_${m}_${d}`,
  legacyAction: (m, d) => `biz_odc_action_${m}_${d}`,
  occ:    (oid, meal, field) =>
    `biz_odc_occ_${oid}_${meal.toLowerCase().replace(/\s+/g, "_")}_${field}`,
  // Month-scoped occupancy values (covers / revenue only — seats/hours/duration
  // are infrastructure and stay shared across months).
  occMonth: (oid, meal, monthId, field) =>
    `biz_odc_occ_${oid}_${meal.toLowerCase().replace(/\s+/g, "_")}_${monthId}_${field}`,
  // Per-outlet × per-meal enable/disable toggle. Default = enabled when unset.
  mealEnabled: (oid, meal) =>
    `biz_odc_meal_enabled_${oid}_${meal.toLowerCase().replace(/\s+/g, "_")}`,
};

// `data[key]` with legacy fallback so users who already typed events under
// the old (un-scoped) key format still see them when viewing the year that
// matches their settings.
function readDayValue(data, year, settingsYear, kind, m, d) {
  const yk = kind === "event" ? k.event(year, m, d)
           : kind === "score" ? k.score(year, m, d)
           : k.action(year, m, d);
  if (data[yk] !== undefined && data[yk] !== "") return data[yk];
  const sameAsSettings = String(settingsYear || "").trim() === String(year);
  if (sameAsSettings) {
    const lk = kind === "event" ? k.legacyEvent(m, d)
             : kind === "score" ? k.legacyScore(m, d)
             : k.legacyAction(m, d);
    return data[lk] || "";
  }
  return data[yk] || "";
}

function parseOutlets(data) {
  try {
    const v = data[k.outletsList];
    if (!v) return DEFAULT_OUTLETS;
    const arr = JSON.parse(v);
    return Array.isArray(arr) && arr.length ? arr : DEFAULT_OUTLETS;
  } catch {
    return DEFAULT_OUTLETS;
  }
}

// ====================================================================
// Demand Calendar tab
// ====================================================================
function DemandCalendarTab({ workspaceId, data, locked, onChange }) {
  const [selected, setSelected] = useState(null); // { mid, day } | null
  const [aiSyncing, setAiSyncing] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiToast, setAiToast] = useState(null);
  const [activeYearIdx, setActiveYearIdx] = useState(0);

  // The settings year acts as the "anchor" year and enables legacy fallback.
  const settingsYear = useMemo(() => {
    const n = parseInt(String(data[k.year] || "").trim(), 10);
    return Number.isFinite(n) && n >= 1900 && n <= 2999 ? n : new Date().getFullYear();
  }, [data]);

  // Build the years tab strip: any year persisted in the list + the settings
  // year + 4 forward years. Dedup + sort ascending. Persisted via biz_odc_years.
  const years = useMemo(() => {
    let saved = [];
    try {
      const v = data[k.yearsList];
      if (v) saved = JSON.parse(v) || [];
    } catch { /* ignore */ }
    const set = new Set();
    saved.forEach((y) => { const n = parseInt(y, 10); if (Number.isFinite(n)) set.add(n); });
    // Always include settings year + 4 forward years
    for (let i = 0; i < 5; i++) set.add(settingsYear + i);
    return Array.from(set).sort((a, b) => a - b);
  }, [data, settingsYear]);

  const yearNum = years[Math.min(activeYearIdx, years.length - 1)] ?? settingsYear;

  const addNextYear = () => {
    const next = (years[years.length - 1] || settingsYear) + 1;
    const list = Array.from(new Set([...years, next])).sort((a, b) => a - b);
    onChange(k.yearsList, JSON.stringify(list));
    // Auto-select the newly added year
    setActiveYearIdx(list.indexOf(next));
  };

  // Read helper that does year-scoping + legacy fallback for current settingsYear.
  const valOf = (kind, m, d) => readDayValue(data, yearNum, settingsYear, kind, m, d);

  const yearStats = useMemo(() => {
    let total = 0, filled = 0, peak = 0, low = 0, sum = 0;
    MONTHS.forEach((m) => {
      const dim = daysInMonth(yearNum, m.monthIdx);
      total += dim;
      for (let d = 1; d <= dim; d++) {
        const s = num(valOf("score", m.id, d));
        if (s > 0) { filled++; sum += s; }
        if (s >= 90) peak++;
        else if (s > 0 && s < 60) low++;
      }
    });
    return {
      total, filled, peak, low,
      avg: filled ? sum / filled : 0,
    };
  }, [data, yearNum]);

  const loadSampleEvents = () => {
    if (locked) return;
    if (!window.confirm("Pre-populate every empty day with Vorion's suggested events & AI actions? Your existing entries will NOT be overwritten.")) return;
    Object.entries(SAMPLE_EVENTS).forEach(([m, days]) => {
      Object.entries(days).forEach(([d, payload]) => {
        if (!data[k.event(yearNum, m, d)])  onChange(k.event(yearNum, m, d), payload.event);
        if (!data[k.score(yearNum, m, d)])  onChange(k.score(yearNum, m, d), String(payload.score));
        if (!data[k.action(yearNum, m, d)]) onChange(k.action(yearNum, m, d), payload.action);
      });
    });
  };

  const clearYear = () => {
    if (locked) return;
    if (!window.confirm(`Clear EVERY day entry for the whole ${yearNum} calendar?`)) return;
    MONTHS.forEach((m) => {
      const dim = daysInMonth(yearNum, m.monthIdx);
      for (let d = 1; d <= dim; d++) {
        onChange(k.event(yearNum, m.id, d), "");
        onChange(k.score(yearNum, m.id, d), "");
        onChange(k.action(yearNum, m.id, d), "");
        // Also clear legacy keys if this is the settings year so they don't reappear
        if (yearNum === settingsYear) {
          onChange(k.legacyEvent(m.id, d), "");
          onChange(k.legacyScore(m.id, d), "");
          onChange(k.legacyAction(m.id, d), "");
        }
      }
    });
    setSelected(null);
  };

  const syncLocalFestiveCalendar = async () => {
    if (locked) return;
    const location = (data[k.loc] || "").trim();
    if (!location) {
      setAiError("Please enter the Location field first (e.g. Pune, Mumbai, Dubai…) so the AI knows which local festivals to load.");
      return;
    }
    if (!window.confirm(
      `Ask Vorion AI to generate the local festive calendar for ${location}? ` +
      `Existing day entries will NOT be overwritten — only empty days are filled.`
    )) return;
    setAiSyncing(true);
    setAiError(null);
    setAiToast(null);
    try {
      const r = await api.post(
        `/workspaces/${workspaceId}/fnb-profile/demand-calendar/ai-sync`,
        {
          location,
          cuisine:     data[k.cuisine]    || null,
          outlet_name: data[k.outletName] || null,
          year:        String(yearNum),
        },
      );
      const events = r.data?.events || [];
      let merged = 0, skipped = 0;
      events.forEach((ev) => {
        const m = ev.month, d = ev.day;
        if (!m || !d) return;
        const evKey = k.event(yearNum, m, d), scKey = k.score(yearNum, m, d), acKey = k.action(yearNum, m, d);
        if (valOf("event", m, d) || valOf("score", m, d) || valOf("action", m, d)) {
          skipped += 1;
          return;
        }
        if (ev.event)  onChange(evKey, ev.event);
        if (ev.score)  onChange(scKey, String(ev.score));
        if (ev.action) onChange(acKey, ev.action);
        merged += 1;
      });
      setAiToast(
        `Vorion AI added ${merged} festive day${merged === 1 ? "" : "s"} for ${location} (${yearNum})` +
        (skipped ? ` · ${skipped} skipped (already filled)` : "")
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI sync failed";
      setAiError(msg);
    } finally {
      setAiSyncing(false);
    }
  };

  // Selected-day editor data
  const sel = selected;
  const selEvent  = sel ? valOf("event",  sel.mid, sel.day) : "";
  const selScore  = sel ? valOf("score",  sel.mid, sel.day) : "";
  const selAction = sel ? valOf("action", sel.mid, sel.day) : "";
  const selLabel  = sel ? `${MONTHS.find(m=>m.id===sel.mid)?.label} ${sel.day}, ${yearNum}` : "";

  return (
    <div className="space-y-3" data-testid="demand-calendar-tab">
      {/* Settings strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { key: k.loc,        label: "Location",        ph: "e.g. Pune" },
          { key: k.outletName, label: "Outlet Name",     ph: "e.g. Vorion Demo Restaurant" },
          { key: k.cuisine,    label: "Primary Cuisine", ph: "e.g. Premium Asian / Indian / Global" },
          { key: k.year,       label: "Demand Year",     ph: "e.g. 2026" },
        ].map((f) => (
          <div key={f.key}>
            <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
              {f.label}
            </label>
            <input
              type="text"
              value={data[f.key] || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              disabled={locked}
              placeholder={f.ph}
              data-testid={`field-${f.key}`}
              className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1 text-xs text-white outline-none disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600"
            />
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={syncLocalFestiveCalendar}
          disabled={locked || aiSyncing}
          data-testid="demand-ai-sync-btn"
          title="Use Vorion AI to load the local festive calendar based on Location"
          className="text-[10px] font-mono uppercase tracking-widest border border-[#D4A968]/60 hover:border-[#D4A968] text-[#D4A968] hover:text-black hover:bg-[#D4A968] rounded px-2.5 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          <span className="text-[11px]">✦</span>
          {aiSyncing ? "Syncing…" : "Sync local festive calendar (AI)"}
        </button>
        <button
          type="button"
          onClick={loadSampleEvents}
          disabled={locked}
          data-testid="demand-load-sample-btn"
          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 hover:border-[#D4A968] text-zinc-300 hover:text-[#D4A968] rounded px-2 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Load sample events
        </button>
        <button
          type="button"
          onClick={clearYear}
          disabled={locked}
          data-testid="demand-clear-year-btn"
          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 hover:border-[#EF4444] text-zinc-300 hover:text-[#EF4444] rounded px-2 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear {yearNum}
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
          <span>Avg score: <span className="text-zinc-200">{yearStats.avg.toFixed(1)}</span></span>
          <span>Peak: <span className="text-[#86EFAC]">{yearStats.peak}</span></span>
          <span>Low: <span className="text-[#FCA5A5]">{yearStats.low}</span></span>
          <span>Filled: <span className="text-zinc-200">{yearStats.filled}/{yearStats.total}</span></span>
        </div>
      </div>

      {/* AI sync status messages */}
      {aiError && (
        <div
          className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded px-2.5 py-1.5 text-[11px] text-[#FCA5A5]"
          data-testid="demand-ai-error"
        >
          {aiError}
        </div>
      )}
      {aiToast && (
        <div
          className="bg-[#10B981]/10 border border-[#10B981]/30 rounded px-2.5 py-1.5 text-[11px] text-[#86EFAC]"
          data-testid="demand-ai-toast"
        >
          {aiToast}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
        <span className="text-zinc-500">Legend:</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#10B981]/40 border border-[#10B981]/60" />Very High</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#38BDF8]/40 border border-[#38BDF8]/60" />High</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#F59E0B]/40 border border-[#F59E0B]/60" />Medium</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#EF4444]/40 border border-[#EF4444]/60" />Low</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-transparent border border-zinc-700" />Empty</span>
        <span className="ml-auto text-zinc-500 italic normal-case tracking-normal">Click any day to edit · {yearNum}</span>
      </div>

      {/* Year tab strip — switch between years; "+ Add year" appends the next */}
      <div className="flex flex-wrap items-center gap-1" data-testid="demand-year-tabs">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">Year</span>
        {years.map((y, idx) => {
          const isActive = idx === activeYearIdx;
          return (
            <button
              key={y}
              type="button"
              onClick={() => setActiveYearIdx(idx)}
              data-testid={`demand-year-tab-${y}`}
              aria-selected={isActive}
              className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded transition-colors tabular-nums ${
                isActive
                  ? "bg-[#D4A968] text-black"
                  : "bg-[#0B0B0E] text-zinc-400 border border-zinc-800 hover:border-[#D4A968]/40 hover:text-zinc-200"
              }`}
            >
              {y}
            </button>
          );
        })}
        <button
          type="button"
          onClick={addNextYear}
          disabled={locked}
          data-testid="demand-add-year-btn"
          title="Add the next year"
          className="text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-dashed border-zinc-700 hover:border-[#D4A968] text-zinc-400 hover:text-[#D4A968] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add year
        </button>
      </div>

      {/* Year wall planner — horizontal 31-day rows per month, matching the Excel layout */}
      <div
        className="bg-[#141417] border border-zinc-800 rounded-lg overflow-x-auto"
        data-testid="demand-year-grid"
      >
        <div className="bg-[#0B3D2E] text-white px-3 py-2 text-[12px] font-semibold tracking-wide">
          VORION AI DEMAND CALENDAR
          <span className="text-[#86EFAC]/80 font-normal ml-2 normal-case tracking-normal text-[11px]">
            · {yearNum}
          </span>
        </div>

        {/* Day-number header */}
        <div
          className="grid bg-[#0B3D2E] text-white text-[10px] font-mono uppercase tracking-widest"
          style={{ gridTemplateColumns: `64px repeat(31, minmax(0, 1fr))` }}
        >
          <div className="px-1.5 py-1 border-r border-[#86EFAC]/30">Day</div>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <div key={d} className="px-0.5 py-1 text-center border-r border-[#86EFAC]/30 tabular-nums">
              {String(d).padStart(2, "0")}
            </div>
          ))}
        </div>

        {/* One month band per row: month label + 31 event cells + AI Action cells */}
        {MONTHS.map((m) => {
          const dim = daysInMonth(yearNum, m.monthIdx);
          return (
            <div
              key={m.id}
              className="border-t border-zinc-800"
              data-testid={`year-month-${m.id}`}
            >
              {/* Event row */}
              <div
                className="grid"
                style={{ gridTemplateColumns: `64px repeat(31, minmax(0, 1fr))` }}
              >
                <div className="bg-[#0F3D2E]/80 text-white px-1.5 py-1 border-r border-zinc-800 flex flex-col justify-center">
                  <div className="text-[10px] font-semibold uppercase">{m.label}</div>
                  <div className="text-[8px] text-[#86EFAC]/70 font-mono tabular-nums">{yearNum}</div>
                </div>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  if (d > dim) {
                    return (
                      <div
                        key={d}
                        className="border-r border-zinc-800/60 bg-zinc-900/70"
                        data-testid={`wp-empty-${m.id}-${d}`}
                      />
                    );
                  }
                  const ev = valOf("event", m.id, d);
                  const score = num(valOf("score", m.id, d));
                  const cls = scoreClass(score);
                  const isSelected = sel && sel.mid === m.id && sel.day === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelected({ mid: m.id, day: d })}
                      data-testid={`year-day-${m.id}-${d}`}
                      title={ev ? `${m.label} ${d}, ${yearNum}: ${ev}${score ? ` (score ${score})` : ""}` : `${m.label} ${d}, ${yearNum} — click to edit`}
                      className={`relative text-left border-r border-zinc-800/60 transition-all ${cls} ${
                        isSelected ? "ring-2 ring-inset ring-[#D4A968] z-10" : "hover:brightness-125"
                      }`}
                      style={{ height: 28 }}
                    >
                      {score > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono tabular-nums">
                          {score}
                        </div>
                      )}
                      {!score && ev && <div className="absolute inset-1.5 rounded-sm bg-[#D4A968]/40" />}
                    </button>
                  );
                })}
              </div>

              {/* AI Action row */}
              <div
                className="grid border-t border-zinc-800/40"
                style={{ gridTemplateColumns: `64px repeat(31, minmax(0, 1fr))` }}
              >
                <div className="bg-[#0F0F12] text-zinc-400 px-1.5 py-1 border-r border-zinc-800 text-[8px] font-mono uppercase tracking-widest flex items-center">
                  AI Action
                </div>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  if (d > dim) {
                    return (
                      <div
                        key={d}
                        className="border-r border-zinc-800/60 bg-zinc-900/70"
                      />
                    );
                  }
                  const ac = valOf("action", m.id, d);
                  const score = num(valOf("score", m.id, d));
                  const cls = score > 0 ? `${scoreClass(score)} opacity-50` : "bg-[#0B0B0E]";
                  const isSelected = sel && sel.mid === m.id && sel.day === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelected({ mid: m.id, day: d })}
                      data-testid={`year-action-${m.id}-${d}`}
                      title={ac ? `${m.label} ${d}, ${yearNum} AI Action: ${ac}` : `${m.label} ${d}, ${yearNum} — click to add action`}
                      className={`relative border-r border-zinc-800/60 transition-all ${cls} ${
                        isSelected ? "ring-2 ring-inset ring-[#D4A968] z-10" : "hover:brightness-125"
                      }`}
                      style={{ height: 16 }}
                    >
                      {ac && <div className="absolute inset-1 rounded-sm bg-[#D4A968]/30" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend explainer */}
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        Hover any cell to see the event & action. Click to edit below. Top row = Event/score. Bottom row = AI Action marker.
      </div>

      {/* Selected-day editor */}
      {sel && (
        <div
          className="bg-[#0F0F12] border border-[#D4A968]/40 rounded-lg p-3"
          data-testid="demand-day-editor"
        >
          <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-zinc-800">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968]">
              Editing · {selLabel}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${scoreClass(num(selScore))}`}>
                {scoreLevel(num(selScore))}
              </span>
              <button
                type="button"
                onClick={() => setSelected(null)}
                data-testid="demand-day-editor-close"
                className="text-zinc-500 hover:text-zinc-200 text-xs"
                title="Close editor"
              >
                ×
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <div className="md:col-span-3">
              <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
                Holiday / Event
              </label>
              <input
                type="text"
                value={selEvent}
                onChange={(e) => onChange(k.event(yearNum, sel.mid, sel.day), e.target.value)}
                disabled={locked}
                data-testid={`field-${k.event(yearNum, sel.mid, sel.day)}`}
                placeholder="Festival, civic event, weather shift…"
                className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1.5 text-sm text-white outline-none disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
                AI Score
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={selScore}
                onChange={(e) => onChange(k.score(yearNum, sel.mid, sel.day), e.target.value)}
                disabled={locked}
                data-testid={`field-${k.score(yearNum, sel.mid, sel.day)}`}
                placeholder="0-100"
                className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1.5 text-sm text-white text-right tabular-nums outline-none disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
                AI Action
              </label>
              <textarea
                value={selAction}
                onChange={(e) => onChange(k.action(yearNum, sel.mid, sel.day), e.target.value)}
                disabled={locked}
                data-testid={`field-${k.action(yearNum, sel.mid, sel.day)}`}
                placeholder="Concise operational action…"
                rows={2}
                className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1.5 text-sm text-white outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600 leading-snug"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ====================================================================
// Occupancy & RevPASH tab
// ====================================================================
function OccupancyTab({ data, locked, onChange }) {
  const outlets = useMemo(() => parseOutlets(data), [data]);
  const setOutlets = (next) => onChange(k.outletsList, JSON.stringify(next));

  // Which outlets are visible in this view. Default: just the first one.
  // Adding/removing outlets here only toggles visibility, not the underlying data.
  const [visibleIds, setVisibleIds] = useState(() => (outlets[0] ? [outlets[0].id] : []));

  // If the underlying outlets list changes (e.g. user adds a brand-new outlet),
  // make sure visibleIds stays valid and seeded with at least the first outlet.
  useEffect(() => {
    const valid = visibleIds.filter((id) => outlets.some((o) => o.id === id));
    if (valid.length === 0 && outlets[0]) {
      setVisibleIds([outlets[0].id]);
    } else if (valid.length !== visibleIds.length) {
      setVisibleIds(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets.map((o) => o.id).join("|")]);

  const visibleOutlets = outlets.filter((o) => visibleIds.includes(o.id));

  const renameOutlet = (id, name) => {
    const next = outlets.map((o) => (o.id === id ? { ...o, name } : o));
    setOutlets(next);
  };
  const removeOutlet = (id) => {
    if (locked) return;
    if (!window.confirm("Remove this outlet? Its values stay in the data but the row will disappear.")) return;
    setOutlets(outlets.filter((o) => o.id !== id));
    setVisibleIds(visibleIds.filter((x) => x !== id));
  };

  // Selected month for per-month covers/revenue. Defaults to Jan.
  const [activeMonth, setActiveMonth] = useState(MONTHS[0].id);

  // Meal toggle helper. Default = ON when key is unset. "0"/"false" => OFF.
  const isMealEnabled = (oid, meal) => {
    const v = data[k.mealEnabled(oid, meal)];
    if (v === undefined || v === "" || v === null) return true;
    return v !== "0" && v !== "false" && v !== false;
  };
  const toggleMeal = (oid, meal) => {
    if (locked) return;
    onChange(k.mealEnabled(oid, meal), isMealEnabled(oid, meal) ? "0" : "1");
  };

  // Read covers/revenue with monthly precedence. Seats/Hours/Duration are
  // infrastructure and shared across months.
  const readMonthly = (oid, meal, field) => {
    const monthly = data[k.occMonth(oid, meal, activeMonth, field)];
    if (monthly !== undefined && monthly !== "") return monthly;
    return data[k.occ(oid, meal, field)] || ""; // legacy fallback
  };

  // Compute one row of metrics (for the currently selected month)
  const computeRow = (oid, meal) => {
    const seats    = num(data[k.occ(oid, meal, "seats")])    || DEFAULT_SEATING[meal]?.seats    || 0;
    const hours    = num(data[k.occ(oid, meal, "hours")])    || DEFAULT_SEATING[meal]?.hours    || 0;
    const duration = num(data[k.occ(oid, meal, "duration")]) || DEFAULT_SEATING[meal]?.duration || 0;

    // Inputs (per-month)
    const budgetedCovers = num(readMonthly(oid, meal, "covers"));               // legacy `covers` = Budgeted Covers
    const actualCovers   = num(readMonthly(oid, meal, "actualCovers"));
    const budgetTotal    = num(readMonthly(oid, meal, "budgetTotal"));
    const totalRevenue   = num(readMonthly(oid, meal, "revenue"));              // legacy `revenue` = Total (Actual) Revenue
    const budgetFoodRev  = num(readMonthly(oid, meal, "budgetFoodRevenue"));
    const actualFoodRev  = num(readMonthly(oid, meal, "actualFoodRevenue"));
    const budgetBevRev   = num(readMonthly(oid, meal, "budgetBevRevenue"));
    const actualBevRev   = num(readMonthly(oid, meal, "actualBevRevenue"));

    // Effective covers for Actual computations — fall back to Budgeted when
    // user hasn't typed Actual Covers yet (preserves existing data semantics).
    const effActualCovers = actualCovers > 0 ? actualCovers : budgetedCovers;

    // Computed APCs
    const budgetAPC      = budgetedCovers > 0 ? budgetTotal     / budgetedCovers : 0;
    const actualAPC      = effActualCovers > 0 ? totalRevenue   / effActualCovers : 0;
    const budgetFoodAPC  = budgetedCovers > 0 ? budgetFoodRev   / budgetedCovers : 0;
    const actualFoodAPC  = effActualCovers > 0 ? actualFoodRev  / effActualCovers : 0;
    const budgetBevAPC   = budgetedCovers > 0 ? budgetBevRev    / budgetedCovers : 0;
    const actualBevAPC   = effActualCovers > 0 ? actualBevRev   / effActualCovers : 0;

    const maxTurnCapacity = (seats > 0 && hours > 0 && duration > 0) ? seats * (hours / duration) : 0;
    const occPct = maxTurnCapacity > 0 ? effActualCovers / maxTurnCapacity : 0;
    const tableTurns = seats > 0 ? effActualCovers / seats : 0;
    const revPASH = (seats > 0 && hours > 0) ? totalRevenue / (seats * hours) : 0;

    return {
      seats, hours, duration,
      budgetedCovers, actualCovers, budgetTotal, totalRevenue,
      budgetFoodRev, actualFoodRev, budgetBevRev, actualBevRev,
      budgetAPC, actualAPC, budgetFoodAPC, actualFoodAPC, budgetBevAPC, actualBevAPC,
      maxTurnCapacity, occPct, tableTurns, revPASH,
      // legacy aliases used elsewhere
      covers: budgetedCovers, revenue: totalRevenue, apc: actualAPC,
    };
  };

  // Aggregate KPIs across visible outlets × meal rows
  const summary = useMemo(() => {
    let totalCovers = 0, totalRevenue = 0, occVals = [], revpashVals = [];
    visibleOutlets.forEach((o) => {
      MEAL_PERIODS.forEach((m) => {
        if (!isMealEnabled(o.id, m)) return;
        const r = computeRow(o.id, m);
        // KPI strip uses ACTUAL numbers (with budgeted fallback for covers).
        totalCovers  += (r.actualCovers > 0 ? r.actualCovers : r.budgetedCovers);
        totalRevenue += r.totalRevenue;
        if (r.occPct > 0) occVals.push(r.occPct);
        if (r.revPASH > 0) revpashVals.push(r.revPASH);
      });
    });
    const avgAPC = totalCovers > 0 ? totalRevenue / totalCovers : 0;
    const avgOcc = occVals.length ? occVals.reduce((a, b) => a + b, 0) / occVals.length : 0;
    const avgRevPASH = revpashVals.length ? revpashVals.reduce((a, b) => a + b, 0) / revpashVals.length : 0;
    return { totalCovers, totalRevenue, avgAPC, avgOcc, avgRevPASH };
  }, [data, visibleOutlets, activeMonth]);

  return (
    <div className="space-y-4" data-testid="occupancy-tab">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="odc-kpi-strip">
        {[
          { label: "Total Covers",  value: fmt(summary.totalCovers) },
          { label: "Total Revenue", value: fmt(summary.totalRevenue) },
          { label: "Actual APC",    value: fmt(summary.avgAPC, 0) },
          { label: "Occupancy",     value: fmtPct(summary.avgOcc) },
          { label: "RevPASH",       value: fmt(summary.avgRevPASH, 1) },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="bg-[#0B0B0E] border border-zinc-800 rounded p-2.5"
            data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
              {kpi.label}
            </div>
            <div className="text-base font-semibold text-[#D4A968] tabular-nums mt-0.5">
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Month tab strip — Covers & Net Revenue are per-month; Seats/Hours/Avg Dine Hrs stay shared */}
      <div
        className="bg-[#0B0B0E] border border-zinc-800 rounded p-1.5 grid grid-cols-12 gap-1"
        data-testid="odc-month-tabs"
      >
        {MONTHS.map((m) => {
          const active = m.id === activeMonth;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveMonth(m.id)}
              data-testid={`odc-month-tab-${m.id}`}
              aria-pressed={active}
              className={
                active
                  ? "py-1.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#D4A968] text-black font-semibold"
                  : "py-1.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#141417] border border-zinc-800 text-zinc-400 hover:text-[#D4A968] hover:border-[#D4A968]/40"
              }
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Per (Outlet × Meal) grid */}
      <div className="space-y-3">
        {visibleOutlets.map((o) => (
          <div
            key={o.id}
            className="bg-[#141417] border border-zinc-800 rounded-lg p-3"
            data-testid={`outlet-block-${o.id}`}
          >
            <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-zinc-800">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4A968] shrink-0">Outlet</span>
              <input
                type="text"
                value={o.name}
                onChange={(e) => renameOutlet(o.id, e.target.value)}
                disabled={locked}
                data-testid={`outlet-name-${o.id}`}
                className="flex-1 min-w-0 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-[#D4A968] text-sm text-zinc-100 outline-none transition-colors disabled:opacity-60"
              />
              {!locked && (
                <button
                  type="button"
                  onClick={() => removeOutlet(o.id)}
                  data-testid={`outlet-remove-${o.id}`}
                  className="shrink-0 text-zinc-500 hover:text-[#EF4444] text-xs font-mono uppercase tracking-widest"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-xs border-separate border-spacing-0">
                <thead>
                  <tr className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                    {[
                      "Meal", "Seats",
                      "Budgeted Covers", "Actual Covers",
                      "Total Budget", "Total Revenue",
                      "Budget APC", "Actual APC",
                      "Budget Food Revenue", "Actual Food Revenue",
                      "Budget Food APC", "Actual Food APC",
                      "Budget Beverage Revenue", "Actual Beverage Revenue",
                      "Budget Beverage APC", "Actual Beverage APC",
                      "Occ %", "RevPASH",
                    ].map((h) => (
                      <th key={h} className="text-left py-1 px-1.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MEAL_PERIODS.map((meal) => {
                    const r = computeRow(o.id, meal);
                    const mealLabel = getMealLabel(data, meal);
                    const renameable = RENAMEABLE_MEALS.has(meal);
                    const enabled = isMealEnabled(o.id, meal);
                    return (
                      <tr
                        key={meal}
                        data-testid={`row-${o.id}-${meal.toLowerCase().replace(/\s+/g,'-')}`}
                        className={enabled ? "" : "opacity-40"}
                      >
                        <td className="py-1 px-1.5 border-t border-zinc-800/70 text-zinc-200 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              aria-label={`${enabled ? "Disable" : "Enable"} ${mealLabel}`}
                              onClick={() => toggleMeal(o.id, meal)}
                              disabled={locked}
                              data-testid={`toggle-${o.id}-${meal.toLowerCase().replace(/\s+/g,'-')}`}
                              title={enabled ? "Disable this meal period" : "Enable this meal period"}
                              className={
                                "relative inline-flex h-3.5 w-7 shrink-0 rounded-full transition-colors outline-none focus:ring-1 focus:ring-[#D4A968] disabled:opacity-50 " +
                                (enabled ? "bg-[#D4A968]" : "bg-zinc-700")
                              }
                            >
                              <span
                                className={
                                  "inline-block h-2.5 w-2.5 rounded-full bg-black transition-transform " +
                                  (enabled ? "translate-x-[14px]" : "translate-x-[2px]") +
                                  " mt-[3px]"
                                }
                              />
                            </button>
                            {renameable ? (
                              <input
                                type="text"
                                value={mealLabel}
                                onChange={(e) => onChange(`biz_odc_meal_label_${meal}`, e.target.value)}
                                disabled={locked}
                                maxLength={32}
                                data-testid={`field-meal-label-${meal}`}
                                className="w-[110px] bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-1.5 py-0.5 text-xs text-zinc-200 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                                title="Rename this meal period"
                              />
                            ) : (
                              <span>{mealLabel}</span>
                            )}
                          </div>
                        </td>

                        {/* Editable cells (Seats is shared; the rest are per-month).
                            Mix of inputs and computed APCs follows the column header order:
                            Seats | Budgeted Covers | Actual Covers | Total Budget | Total Revenue |
                            Budget APC* | Actual APC* | Budget Food Rev | Actual Food Rev |
                            Budget Food APC* | Actual Food APC* | Budget Bev Rev | Actual Bev Rev |
                            Budget Bev APC* | Actual Bev APC*. (* = computed) */}
                        {(() => {
                          // helper renderers
                          const inputCell = (field, monthly = true) => {
                            const key = monthly
                              ? k.occMonth(o.id, meal, activeMonth, field)
                              : k.occ(o.id, meal, field);
                            const value = monthly ? readMonthly(o.id, meal, field) : (data[key] || "");
                            const placeholder = field === "seats" ? String(DEFAULT_SEATING[meal]?.seats ?? "") : "";
                            return (
                              <td key={field} className="py-1 px-1 border-t border-zinc-800/70">
                                <input
                                  type="number"
                                  step="1"
                                  value={value}
                                  onChange={(e) => onChange(key, e.target.value)}
                                  disabled={locked || !enabled}
                                  placeholder={placeholder}
                                  data-testid={`field-${key}`}
                                  className="w-full min-w-[88px] bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-1.5 py-0.5 text-xs text-white text-right outline-none disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-700 tabular-nums"
                                />
                              </td>
                            );
                          };
                          const computedCell = (label, value, colour = "text-zinc-300") => (
                            <td key={label} className={`py-1 px-1.5 border-t border-zinc-800/70 ${colour} text-right tabular-nums`}>
                              {fmt(value, 0)}
                            </td>
                          );
                          return (
                            <>
                              {inputCell("seats", false)}
                              {inputCell("covers")}{/* Budgeted Covers (legacy `covers` key) */}
                              {inputCell("actualCovers")}
                              {inputCell("budgetTotal")}
                              {inputCell("revenue")}{/* Total Revenue (legacy `revenue` key) */}
                              {computedCell("budgetAPC", r.budgetAPC, "text-zinc-400")}
                              {computedCell("actualAPC", r.actualAPC, "text-zinc-200")}
                              {inputCell("budgetFoodRevenue")}
                              {inputCell("actualFoodRevenue")}
                              {computedCell("budgetFoodAPC", r.budgetFoodAPC, "text-zinc-400")}
                              {computedCell("actualFoodAPC", r.actualFoodAPC, "text-zinc-200")}
                              {inputCell("budgetBevRevenue")}
                              {inputCell("actualBevRevenue")}
                              {computedCell("budgetBevAPC", r.budgetBevAPC, "text-zinc-400")}
                              {computedCell("actualBevAPC", r.actualBevAPC, "text-zinc-200")}
                            </>
                          );
                        })()}

                        {/* Computed tail — Occ % & RevPASH */}
                        <td className="py-1 px-1.5 border-t border-zinc-800/70 text-[#FCD34D] text-right tabular-nums">{fmtPct(r.occPct)}</td>
                        <td className="py-1 px-1.5 border-t border-zinc-800/70 text-[#86EFAC] text-right tabular-nums">{fmt(r.revPASH, 1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* AI recommendation row */}
            <div className="mt-2 space-y-1">
              {MEAL_PERIODS.map((meal) => {
                if (!isMealEnabled(o.id, meal)) return null;
                const r = computeRow(o.id, meal);
                const rec = occRecommendation(r.occPct);
                if (r.occPct <= 0) return null;
                return (
                  <div key={meal} className="text-[11px] text-zinc-300 leading-snug">
                    <span className="text-zinc-500 font-mono uppercase tracking-widest mr-1.5">{getMealLabel(data, meal)}:</span>
                    {rec}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ====================================================================
// Analytics tab — Budget vs Actual donuts grouped by metric, one per meal.
// Re-uses storage keys from OccupancyTab via `readMonthly` semantics.
// ====================================================================
function AnalyticsTab({ data, onChange }) {
  const outlets = useMemo(() => parseOutlets(data), [data]);
  const [outletId, setOutletId] = useState(() => outlets[0]?.id || "");
  const [activeMonth, setActiveMonth] = useState(MONTHS[0].id);

  // Keep the selected outlet pinned to a real one as the list changes
  useEffect(() => {
    if (!outlets.some((o) => o.id === outletId) && outlets[0]) {
      setOutletId(outlets[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets.map((o) => o.id).join("|")]);

  const readMonthly = (oid, meal, field) => {
    const monthly = data[k.occMonth(oid, meal, activeMonth, field)];
    if (monthly !== undefined && monthly !== "") return monthly;
    return data[k.occ(oid, meal, field)] || "";
  };
  const isMealEnabled = (oid, meal) => {
    const v = data[k.mealEnabled(oid, meal)];
    if (v === undefined || v === "" || v === null) return true;
    return v !== "0" && v !== "false" && v !== false;
  };

  // Per-meal computed numbers (matches OccupancyTab.computeRow semantics)
  const computeMealRow = (oid, meal) => {
    const budgetedCovers = num(readMonthly(oid, meal, "covers"));
    const actualCovers   = num(readMonthly(oid, meal, "actualCovers"));
    const budgetTotal    = num(readMonthly(oid, meal, "budgetTotal"));
    const totalRevenue   = num(readMonthly(oid, meal, "revenue"));
    const budgetFoodRev  = num(readMonthly(oid, meal, "budgetFoodRevenue"));
    const actualFoodRev  = num(readMonthly(oid, meal, "actualFoodRevenue"));
    const budgetBevRev   = num(readMonthly(oid, meal, "budgetBevRevenue"));
    const actualBevRev   = num(readMonthly(oid, meal, "actualBevRevenue"));
    const effActualCovers = actualCovers > 0 ? actualCovers : budgetedCovers;
    return {
      budgetedCovers, actualCovers, budgetTotal, totalRevenue,
      budgetFoodRev, actualFoodRev, budgetBevRev, actualBevRev,
      budgetAPC:     budgetedCovers > 0 ? budgetTotal     / budgetedCovers : 0,
      actualAPC:     effActualCovers > 0 ? totalRevenue   / effActualCovers : 0,
      budgetFoodAPC: budgetedCovers > 0 ? budgetFoodRev   / budgetedCovers : 0,
      actualFoodAPC: effActualCovers > 0 ? actualFoodRev  / effActualCovers : 0,
      budgetBevAPC:  budgetedCovers > 0 ? budgetBevRev    / budgetedCovers : 0,
      actualBevAPC:  effActualCovers > 0 ? actualBevRev   / effActualCovers : 0,
    };
  };

  // 7 metric groups (title + budget-field + actual-field accessors)
  const METRICS = [
    { id: "covers",  title: "Covers",            budget: (r) => r.budgetedCovers, actual: (r) => r.actualCovers, fmt: (v) => fmt(v, 0) },
    { id: "budget",  title: "Budget vs Revenue", budget: (r) => r.budgetTotal,    actual: (r) => r.totalRevenue, fmt: (v) => fmt(v, 0) },
    { id: "apc",     title: "Total APC",         budget: (r) => r.budgetAPC,      actual: (r) => r.actualAPC,    fmt: (v) => fmt(v, 0) },
    { id: "foodRev", title: "Food Revenue",      budget: (r) => r.budgetFoodRev,  actual: (r) => r.actualFoodRev, fmt: (v) => fmt(v, 0) },
    { id: "foodAPC", title: "Food APC",          budget: (r) => r.budgetFoodAPC,  actual: (r) => r.actualFoodAPC, fmt: (v) => fmt(v, 0) },
    { id: "bevRev",  title: "Beverage Revenue",  budget: (r) => r.budgetBevRev,   actual: (r) => r.actualBevRev,  fmt: (v) => fmt(v, 0) },
    { id: "bevAPC",  title: "Beverage APC",      budget: (r) => r.budgetBevAPC,   actual: (r) => r.actualBevAPC,  fmt: (v) => fmt(v, 0) },
  ];

  const enabledMeals = outletId
    ? MEAL_PERIODS.filter((m) => isMealEnabled(outletId, m))
    : [];

  // Pre-compute every row once
  const meals = enabledMeals.map((m) => ({
    meal: m,
    label: getMealLabel(data, m),
    row: computeMealRow(outletId, m),
  }));

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Month strip — re-uses the same look as Occupancy tab */}
      <div className="bg-[#0B0B0E] border border-zinc-800 rounded p-1.5 grid grid-cols-12 gap-1" data-testid="analytics-month-tabs">
        {MONTHS.map((m) => {
          const active = m.id === activeMonth;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveMonth(m.id)}
              data-testid={`analytics-month-tab-${m.id}`}
              aria-pressed={active}
              className={active
                ? "py-1.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#D4A968] text-black font-semibold"
                : "py-1.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#141417] border border-zinc-800 text-zinc-400 hover:text-[#D4A968] hover:border-[#D4A968]/40"
              }
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {outletId === "" ? (
        <div className="text-xs text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded">
          No outlets configured.
        </div>
      ) : enabledMeals.length === 0 ? (
        <div className="text-xs text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded" data-testid="analytics-empty">
          All meal periods are disabled for this outlet. Enable at least one in the Occupancy & RevPASH tab.
        </div>
      ) : (
        // 7 metric cards, each shows one donut per enabled meal
        <div className="space-y-3" data-testid="analytics-grid">
          {METRICS.map((mt) => (
            <div
              key={mt.id}
              className="bg-[#141417] border border-zinc-800 rounded-lg p-3"
              data-testid={`analytics-card-${mt.id}`}
            >
              <div className="flex items-baseline justify-between mb-3 pb-1.5 border-b border-zinc-800">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4A968]">{mt.title}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                  Budget <span className="inline-block w-2 h-2 rounded-sm bg-[#3F3F46] align-middle ml-1 mr-2" />
                  Actual <span className="inline-block w-2 h-2 rounded-sm bg-[#D4A968] align-middle ml-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {meals.map(({ meal, label, row }) => (
                  <BudgetActualDonut
                    key={meal}
                    title={label}
                    budget={mt.budget(row)}
                    actual={mt.actual(row)}
                    formatValue={mt.fmt}
                    testid={`donut-${mt.id}-${meal}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact dual-segment donut: Budget vs Actual. Pure SVG so no extra bundle weight.
function BudgetActualDonut({ title, budget, actual, formatValue, testid }) {
  const b = Math.max(0, Number(budget) || 0);
  const a = Math.max(0, Number(actual) || 0);
  const total = b + a;
  // If both empty, render an "empty" ring so the layout doesn't jump.
  const bPct = total > 0 ? b / total : 0.5;
  const aPct = total > 0 ? a / total : 0.5;
  const isEmpty = total === 0;

  // SVG donut geometry — 64 viewBox, stroke makes the ring.
  const r = 22;
  const c = 2 * Math.PI * r;       // circumference
  const bLen = c * bPct;
  const aLen = c * aPct;

  // Variance for centre value
  const variancePct = b > 0 ? (a - b) / b : 0;
  const varColour =
    isEmpty ? "text-zinc-600"
    : a >= b ? "text-[#86EFAC]"
    : "text-[#FCA5A5]";

  return (
    <div className="flex flex-col items-center text-center gap-1" data-testid={testid}>
      <div className="relative w-[88px] h-[88px]">
        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#27272A" strokeWidth="8" />
          {!isEmpty && (
            <>
              {/* Budget segment (zinc) */}
              <circle
                cx="32" cy="32" r={r} fill="none"
                stroke="#3F3F46" strokeWidth="8"
                strokeDasharray={`${bLen} ${c - bLen}`}
                strokeDashoffset="0"
              />
              {/* Actual segment (gold) — starts after Budget */}
              <circle
                cx="32" cy="32" r={r} fill="none"
                stroke="#D4A968" strokeWidth="8"
                strokeDasharray={`${aLen} ${c - aLen}`}
                strokeDashoffset={-bLen}
              />
            </>
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className={`text-[10px] font-mono ${varColour}`}>
            {isEmpty ? "—" : `${variancePct >= 0 ? "+" : ""}${(variancePct * 100).toFixed(0)}%`}
          </div>
        </div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 leading-tight">{title}</div>
      <div className="text-[9px] font-mono text-zinc-500 leading-tight">
        <span className="text-zinc-400">B</span> {formatValue(b)} <span className="text-zinc-700 mx-0.5">·</span>
        <span className="text-[#D4A968]">A</span> {formatValue(a)}
      </div>
    </div>
  );
}

// ====================================================================
// Top-level component
// ====================================================================
export default function OutletDemandCalendar({ workspaceId, data, locked, onChange }) {
  const [tab, setTab] = useState("demand");
  return (
    <div
      data-testid="outlet-demand-calendar"
      className="bg-[#141417] border border-zinc-800 rounded-lg p-3 min-w-0"
    >
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-zinc-800">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968]">
          Outlet Demand Calendar
          <span className="ml-2 text-zinc-500 normal-case tracking-normal text-[10px]">
            · Vorion Revenue Command Center
          </span>
        </div>
      </div>

      <div role="tablist" className="flex gap-1 mb-3" data-testid="odc-tabs">
        {[
          { id: "demand",    label: "Demand Calendar" },
          { id: "occupancy", label: "Occupancy & RevPASH" },
          { id: "analytics", label: "Analytics" },
        ].map((t) => {
          const isActive = t.id === tab;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(t.id)}
              data-testid={`odc-tab-${t.id}`}
              className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded transition-colors ${
                isActive ? "bg-[#D4A968] text-black" : "bg-[#0B0B0E] text-zinc-400 border border-zinc-800 hover:border-[#D4A968]/40 hover:text-zinc-200"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "demand"    && <DemandCalendarTab workspaceId={workspaceId} data={data} locked={locked} onChange={onChange} />}
      {tab === "occupancy" && <OccupancyTab     data={data} locked={locked} onChange={onChange} />}
      {tab === "analytics" && <AnalyticsTab     data={data} onChange={onChange} />}
    </div>
  );
}
